import { z } from 'zod';

/**
 * Anbindungen der Händler = Produktquellen (mp_conn, con_katalog der PHP-Middleware, api/lib_connectors.php).
 * Jede Quelle liefert Produkte im gemeinsamen Format (QuellProdukt); daraus entstehen Produkte des Marktplatzes.
 */

export interface AnbindungFeld {
  schluessel: string;
  label: string;
  art: 'text' | 'zahl' | 'geheim';
  platzhalter: string;
  /** optional = darf leer bleiben (z. B. MwSt.-Satz mit Standard 19) */
  optional?: boolean;
}

export interface AnbindungSystem {
  art: string;
  name: string;
  /** direkt anbindbar; sonst Hinweis auf den Weg über Feed oder Datei */
  aktiv: boolean;
  per?: 'feed';
  formate?: string;
  felder: AnbindungFeld[];
  hilfe: string;
  /** Verbindung nur über die App „DXL Marketplace“ mit dem Händler-Code (kein Zugangsformular) */
  ueberApp?: boolean;
}

export interface AnbindungGruppe {
  gruppe: 'shop' | 'erp' | 'pim' | 'feed';
  titel: string;
  systeme: AnbindungSystem[];
}

const f = (schluessel: string, label: string, art: AnbindungFeld['art'] = 'text', platzhalter = '', optional = false): AnbindungFeld => ({
  schluessel, label, art, platzhalter, ...(optional ? { optional } : {}),
});
const URL_FELD = f('url', 'Shop-Adresse', 'text', 'https://www.mein-shop.de');

/** Katalog wie con_katalog() – Texte unverändert aus der PHP-Middleware */
export const ANBINDUNG_KATALOG: AnbindungGruppe[] = [
  {
    gruppe: 'shop',
    titel: 'Shopsysteme',
    systeme: [
      {
        // Shopify vergibt für eigene Apps keine Admin-API-Schlüssel (shpat_…) mehr im Shop-Admin – Verbindung über die App
        art: 'shopify', name: 'Shopify', aktiv: true, ueberApp: true,
        felder: [],
        hilfe: 'Über die App „DXL Marketplace“ mit Ihrem Händler-Code – Artikel, Bestand, Bestellungen und Versand laufen automatisch.',
      },
      {
        art: 'shopware6', name: 'Shopware 6', aktiv: true,
        felder: [URL_FELD, f('client_id', 'Zugangsschlüssel-ID', 'text', 'SWIA…'), f('client_secret', 'Sicherheitsschlüssel', 'geheim')],
        hilfe: 'Shopware-Administration → Einstellungen → System → Integrationen: Integration mit Lesezugriff anlegen.',
      },
      {
        art: 'woocommerce', name: 'WooCommerce', aktiv: true,
        felder: [URL_FELD, f('key', 'Consumer Key', 'text', 'ck_…'), f('secret', 'Consumer Secret', 'geheim', 'cs_…')],
        hilfe: 'WordPress → WooCommerce → Einstellungen → Erweitert → REST-API: Schlüssel mit Leserechten erzeugen.',
      },
      {
        art: 'magento2', name: 'Magento 2 / Adobe Commerce', aktiv: true,
        felder: [URL_FELD, f('token', 'Access-Token der Integration', 'geheim')],
        hilfe: 'System → Erweiterungen → Integrationen: Integration mit Zugriff auf Katalog und Bestand anlegen und aktivieren.',
      },
      {
        art: 'prestashop', name: 'PrestaShop', aktiv: true,
        felder: [URL_FELD, f('key', 'Webservice-Schlüssel', 'geheim'), f('mwst', 'MwSt.-Satz für Bruttopreise (%)', 'zahl', '19', true)],
        hilfe: 'Erweiterte Einstellungen → Webservice: aktivieren, Schlüssel mit GET-Recht für products, images und stock_availables anlegen.',
      },
      { art: 'jtlshop', name: 'JTL-Shop, Gambio, OXID, xt:Commerce', aktiv: false, per: 'feed', felder: [], hilfe: 'Über den Exportmanager einen Google-Shopping- oder CSV-Export als Feed-URL bereitstellen.' },
    ],
  },
  {
    gruppe: 'erp',
    titel: 'ERP-Systeme',
    systeme: [
      { art: 'jtlwawi', name: 'JTL-Wawi', aktiv: false, per: 'feed', felder: [], hilfe: 'Bis zur direkten Anbindung: JTL-Ameise-Export als CSV oder Feed, Bestand und Preise über die Händler-API.' },
      { art: 'plentymarkets', name: 'plentymarkets', aktiv: false, per: 'feed', felder: [], hilfe: 'Bis zur direkten Anbindung: Elastischer Export als Feed-URL, Bestand und Preise über die Händler-API.' },
      { art: 'xentral', name: 'Xentral', aktiv: false, per: 'feed', felder: [], hilfe: 'Bis zur direkten Anbindung: Artikel-Export als CSV/XLSX, Bestand und Preise über die Händler-API.' },
      { art: 'sapb1', name: 'SAP Business One', aktiv: false, per: 'feed', felder: [], hilfe: 'Bis zur direkten Anbindung: Artikelexport als CSV/XLSX, Bestand und Preise über die Händler-API.' },
    ],
  },
  {
    gruppe: 'pim',
    titel: 'PIM-Systeme',
    systeme: [
      { art: 'akeneo', name: 'Akeneo', aktiv: false, per: 'feed', felder: [], hilfe: 'Bis zur direkten Anbindung: Export-Profil als CSV/XLSX oder Feed-URL.' },
      { art: 'pimcore', name: 'Pimcore', aktiv: false, per: 'feed', felder: [], hilfe: 'Bis zur direkten Anbindung: Datahub- oder CSV-Export als Feed-URL.' },
      { art: 'plytix', name: 'Plytix', aktiv: false, per: 'feed', felder: [], hilfe: 'Bis zur direkten Anbindung: Channel-Feed (CSV/XML) als Feed-URL.' },
    ],
  },
  {
    gruppe: 'feed',
    titel: 'Feeds & Dateien',
    systeme: [
      {
        art: 'feed', name: 'Feed-URL', aktiv: true, formate: 'XML · CSV · TXT · XLSX',
        felder: [f('feed_url', 'Adresse des Feeds', 'text', 'https://…/produkte.xml')],
        hilfe: 'Google-Merchant-XML oder eine Tabelle (CSV, TXT, XLSX), die regelmäßig neu abgerufen wird.',
      },
      {
        art: 'datei', name: 'Datei hochladen', aktiv: true, formate: 'XML · CSV · TXT · XLSX',
        felder: [], hilfe: 'Einmaliger Import aus einer Datei. Für laufende Aktualisierung Feed-URL oder Händler-API verwenden.',
      },
    ],
  },
];

/** Interne Art: Produkte, die der Händler über die Shopify-App „DXL Marketplace“ überträgt (shop_uebertragen) */
export const ANBINDUNG_SHOP_APP = 'shopify_app';

export const anbindungSystem = (art: string): (AnbindungSystem & { gruppe: AnbindungGruppe['gruppe'] }) | null => {
  for (const g of ANBINDUNG_KATALOG) for (const s of g.systeme) if (s.art === art) return { ...s, gruppe: g.gruppe };
  return null;
};

/** Shop-Systeme mit eigener Schnittstelle (Abruf über app.haendlerQuelle) */
export const SHOP_ARTEN = ['shopify', 'shopware6', 'woocommerce', 'magento2', 'prestashop'] as const;
export type ShopArt = (typeof SHOP_ARTEN)[number];
export const istShopArt = (art: string): art is ShopArt => (SHOP_ARTEN as readonly string[]).includes(art);

/* ------------------------------------------------------------ Gemeinsames Produktformat */

/** Ein Artikel aus der Quelle (ext_id, sku, ean, title, … der PHP-Middleware) */
export interface QuellProdukt {
  extId: string;
  sku: string;
  ean: string;
  titel: string;
  beschreibung: string;
  preis: number | null;
  bestand: number | null;
  bilder: string[];
  marke: string;
  kategorie: string;
  gewicht: number | null;
  /** alle Spalten bzw. Felder der Quelle – für die Zuordnung der Produktfelder */
  rohdaten: Record<string, string>;
}

/* ------------------------------------------------------------ Spaltenzuordnung (Tabellen) */

/** Zielfelder der Spaltenzuordnung (con_upload → felder) */
export const TABELLEN_FELDER = {
  sku: 'Artikelnummer (SKU)',
  ean: 'EAN/GTIN',
  title: 'Titel',
  description: 'Beschreibung',
  price: 'Preis (brutto)',
  bestand: 'Bestand',
  image: 'Bild',
  images_more: 'Weitere Bilder',
  brand: 'Marke',
  category: 'Kategorie',
  weight: 'Gewicht',
} as const;
export type TabellenFeld = keyof typeof TABELLEN_FELDER;

/** Bekannte Spaltennamen (TAB_SPALTEN: deutsch/englisch, Google, JTL-Ameise, plentymarkets, Akeneo …) */
export const TABELLEN_SPALTEN: Record<TabellenFeld, string[]> = {
  sku: ['sku', 'artikelnummer', 'artnr', 'art.-nr.', 'art-nr', 'artikel-nr.', 'mpn', 'item_number', 'reference', 'productnumber', 'variation.number', 'model', 'id'],
  ean: ['ean', 'gtin', 'ean13', 'ean/gtin', 'barcode', 'upc', 'variation.barcode'],
  title: ['title', 'titel', 'name', 'artikelname', 'produktname', 'bezeichnung', 'product_name', 'item.name'],
  description: ['description', 'beschreibung', 'artikelbeschreibung', 'langbeschreibung', 'body_html', 'long_description'],
  price: ['sale_price', 'price', 'preis', 'vk', 'vk brutto', 'brutto', 'verkaufspreis', 'bruttopreis', 'uvp'],
  bestand: ['bestand', 'stock', 'quantity', 'qty', 'lagerbestand', 'menge', 'verfuegbar', 'verfügbar', 'availability_count', 'stock_quantity'],
  image: ['image_link', 'image', 'bild', 'bild1', 'bild 1', 'bild-url', 'image_url', 'main_image'],
  images_more: ['additional_image_link', 'additional_image_links', 'weitere bilder', 'bilder', 'images'],
  brand: ['brand', 'marke', 'hersteller', 'vendor', 'manufacturer'],
  category: ['product_type', 'google_product_category', 'category', 'kategorie', 'warengruppe'],
  weight: ['weight', 'gewicht', 'shipping_weight'],
};

/** Feld → Spaltenindex (0-basiert) */
export type Spaltenzuordnung = Partial<Record<TabellenFeld, number>>;

/** Kopfzeile automatisch zuordnen (tab_auto_map) */
export function spaltenAutomatisch(kopf: string[]): Spaltenzuordnung {
  const norm = kopf.map((h) => h.trim().replace(/^["'\s]+|["'\s]+$/g, '').toLowerCase());
  const map: Spaltenzuordnung = {};
  const belegt = new Set<number>();
  for (const [feld, namen] of Object.entries(TABELLEN_SPALTEN) as [TabellenFeld, string[]][]) {
    for (const n of namen) {
      const k = norm.indexOf(n);
      if (k >= 0 && !belegt.has(k)) {
        map[feld] = k;
        belegt.add(k);
        break;
      }
    }
  }
  return map;
}

/** Preis aus Text (mp_parse_price): „1.234,56 EUR“ → 1234.56, „1,234.56“ → 1234.56 */
export function preisLesen(roh: string): number {
  let s = roh.trim();
  if (!s) return 0;
  s = s.replace(/[A-Z]{3}/gi, '').replace(/[^\d,.-]/g, '');
  if (/,\d{1,2}$/.test(s)) s = s.replace(/\./g, '').replace(',', '.');
  else s = s.replace(/,/g, '');
  const n = Number.parseFloat(s);
  return Number.isFinite(n) ? n : 0;
}

/* ------------------------------------------------------------ Eingaben */

export const ABGLEICH_INTERVALLE = { 0: 'Aus', 15: 'Alle 15 Minuten', 30: 'Alle 30 Minuten', 60: 'Stündlich', 240: 'Alle 4 Stunden', 1440: 'Täglich' } as const;
export type AbgleichIntervall = 0 | 15 | 30 | 60 | 240 | 1440;

export const FEHLENDE_ARTIKEL = {
  behalten: 'Unverändert lassen (nur im Bericht melden)',
  bestand_null: 'Bestand auf 0 setzen',
  entfernen: 'Produkt auf „Entfernt“ setzen',
} as const;
export type FehlendeArtikel = keyof typeof FEHLENDE_ARTIKEL;

export const ZEITPLAN_MODI = {
  abgleich: 'Nur Bestand und Preis abgleichen',
  import: 'Vollständig importieren (neue, geänderte und entfernte Artikel)',
} as const;
export type ZeitplanModus = keyof typeof ZEITPLAN_MODI;

const Zuordnung = z.record(z.enum(Object.keys(TABELLEN_FELDER) as [TabellenFeld, ...TabellenFeld[]]), z.number().int().min(0).max(500).nullable()).default({});

export const AnbindungSpeichernSchema = z.object({
  art: z.string().trim().min(1).max(40),
  name: z.string().trim().max(120).default(''),
  /** Werte der Zugangsfelder; geheime Felder leer oder „••••••••“ = bisherigen Wert behalten */
  felder: z.record(z.string().max(60), z.string().trim().max(2000)).default({}),
  /** nur für Datei und Feed (Tabellen) */
  zuordnung: Zuordnung.optional(),
  /** Datei-Anbindung: zuvor hochgeladene Datei */
  dateiId: z.string().uuid().optional(),
});
export type AnbindungSpeichernEingabe = z.input<typeof AnbindungSpeichernSchema>;

export const AnbindungAbgleichSchema = z.object({
  intervall: z.union([z.literal(0), z.literal(15), z.literal(30), z.literal(60), z.literal(240), z.literal(1440)]),
  bestand: z.boolean().default(true),
  preis: z.boolean().default(true),
  modus: z.enum(['abgleich', 'import']).default('abgleich'),
  fehlende: z.enum(['behalten', 'bestand_null', 'entfernen']).default('behalten'),
});
export type AnbindungAbgleichEingabe = z.input<typeof AnbindungAbgleichSchema>;

export const AnbindungLaufSchema = z.object({ art: z.enum(['import', 'abgleich']).default('import') });

export const GEHEIM_PLATZHALTER = '••••••••';

/* ------------------------------------------------------------ Antworten */

export type LaufArt = 'import' | 'abgleich';
export type LaufStatus = 'laeuft' | 'ok' | 'fehler';

export interface AnbindungLauf {
  id: string;
  art: LaufArt;
  ausloeser: 'haendler' | 'zeitplan';
  status: LaufStatus;
  gestartetAm: string;
  beendetAm: string | null;
  gelesen: number;
  neu: number;
  geaendert: number;
  unveraendert: number;
  entfernt: number;
  maengel: number;
  fehlerhaft: number;
  unbekannt: number;
  meldung: string;
  /** erste Fehler mit Zeile bzw. SKU */
  fehler: { zeile: number | null; sku: string; grund: string }[];
}

export interface Anbindung {
  id: string;
  art: string;
  name: string;
  systemName: string;
  /** Host des Shops bzw. Feeds, Dateiname */
  quelle: string;
  /** Zugangsfelder: nicht geheime im Klartext, geheime als „••••••••“ (oder leer) */
  felder: Record<string, string>;
  zuordnung: Spaltenzuordnung | null;
  datei: { name: string; groesse: number; typ: string; hochgeladenAm: string } | null;
  verbundenAm: string;
  abgleich: { intervall: AbgleichIntervall; bestand: boolean; preis: boolean; modus: ZeitplanModus; fehlende: FehlendeArtikel };
  naechsterLauf: string | null;
  laeuft: boolean;
  artikel: number;
  letzterLauf: AnbindungLauf | null;
}

export interface AnbindungenUebersicht {
  katalog: AnbindungGruppe[];
  anbindungen: Anbindung[];
  /** darf der angemeldete Zugang trennen und Schlüssel verwalten (Inhaber) */
  inhaber: boolean;
  shopApp: ShopAppStatus;
}

export interface AnbindungVorschau {
  /** shop = Schnittstelle eines Shop-Systems */
  typ: 'csv' | 'xlsx' | 'xml' | 'shop';
  kopf: string[] | null;
  zeilen: string[][];
  zuordnung: Spaltenzuordnung | null;
  /** erste Artikel nach der Zuordnung */
  beispiele: { sku: string; titel: string; preis: number | null; bestand: number | null; bild: string }[];
}

export interface DateiHochgeladen extends AnbindungVorschau {
  typ: 'csv' | 'xlsx' | 'xml';
  dateiId: string;
  name: string;
  groesse: number;
}

export interface AnbindungGespeichert {
  anbindung: Anbindung;
  vorschau: AnbindungVorschau;
}

export interface LaufListe {
  eintraege: AnbindungLauf[];
  weiter: string | null;
}

/* ------------------------------------------------------------ Shopify-App des Händlers (vorbereitet) */

export interface ShopAppStatus {
  code: string | null;
  shop: string | null;
  verbundenAm: string | null;
  /** Schnittstelle zur App bereit (sonst Hinweis „folgt“) */
  verfuegbar: boolean;
  /** Anbindung der übertragenen Artikel (für die Zuordnung der Produkteigenschaften), null = noch nichts übertragen */
  anbindungId: string | null;
}

export interface ShopAppArtikel {
  id: string;
  titel: string;
  variante: string;
  sku: string;
  ean: string;
  preis: number;
  bestand: number | null;
  bild: string;
  /** schon im Marktplatz (über die SKU) */
  marktplatz: { id: string; status: string } | null;
}

export interface ShopAppKatalog {
  produkte: ShopAppArtikel[];
  weiter: string | null;
  shop: string;
}

export const ShopAppKatalogSchema = z.object({ q: z.string().trim().max(100).default(''), cursor: z.string().max(500).default('') });
export const ShopAppUebertragenSchema = z.object({ ids: z.array(z.string().min(1).max(100)).min(1).max(250) });
export const ShopAppZurueckSchema = z.object({ produktId: z.string().uuid() });

/* ------------------------------------------------------------ API-Schlüssel des Händlers */

export const ApiSchluesselSchema = z.object({ name: z.string().trim().min(1, 'Bitte einen Namen angeben.').max(80) });
export type ApiSchluesselEingabe = z.input<typeof ApiSchluesselSchema>;

export interface ApiSchluessel {
  id: string;
  name: string;
  /** Anfang des Schlüssels zum Wiedererkennen, z. B. „dxk_3f9a1c…“ */
  praefix: string;
  erstelltAm: string;
  zuletztGenutzt: string | null;
}

/** Nur direkt nach dem Erzeugen: der Klartext wird nicht gespeichert */
export interface ApiSchluesselNeu extends ApiSchluessel {
  schluessel: string;
}

export const API_SCHLUESSEL_MUSTER = /^dxk_[a-f0-9]{40}$/;
